import type { RouteRecordRaw } from "vue-router"
import { routePaths } from "@/router/routePaths"
import { CX_CORE_FEATURES_ENUM, type Features } from "@/common/util/featuresUtil"
import { guardedImport } from "@/router/guardedImport"
import { EnumWorkspaceId } from "@/components/AppSideNav/workspace/workspaces"
import { EnumCertificatePageNames } from "@/modules/applications/certificateManagement/interfaces"
import { EnumPcfPageNames } from "@/modules/applications/pcfExchange/interfaces"
import { EnumPcfV9PageNames } from "@/modules/applications/pcfExchangeV9/interfaces"
import { EnumBnPageNames } from "@/modules/applications/blockNotifications/interfaces/index"
import { EnumTraceabilityPageNames } from "@/modules/applications/traceability/interfaces"
import { EnumEcuPageNames } from "@/modules/applications/electronicControlUnit/interfaces/index"
import { EnumCSKPISPageNames } from "@/modules/applications/cybersecurityKPIs/interfaces/index"

const ConfigureOffer = () => import("@/modules/provide/create-offer/pages/ConfigureOffer.vue")
const SearchCatalog = () => import("@/modules/consume/search-offer/pages/SearchCatalog.vue")
const AssetsPage = () => import("@/modules/provide/assets-overview/pages/AssetsPage.vue")
const Jobs = () => import("@/modules/monitor/jobs/pages/Jobs.vue")
const Connectors = () => import("@/modules/govern/connectors/pages/Connectors.vue")
const Policies = () => import("@/modules/govern/policies/pages/Policies.vue")

/**
 * Builds an app route whose landing page is the index child. `meta.pageName`
 * carries the sub-page identity that shared pages (catalog, offer wizard) read
 * to configure themselves.
 */
const appRoute = (
  app: { path: string; pathName: string },
  feature: Features,
  workspace: EnumWorkspaceId,
  landing: NonNullable<RouteRecordRaw["component"]>,
  children: RouteRecordRaw[]
): RouteRecordRaw => ({
  path: app.path,
  component: guardedImport(feature, () => import("@/layout/AbstractRouteView.vue")),
  meta: { isProtected: true, workspace },
  children: [
    {
      path: "",
      name: app.pathName,
      component: landing,
      meta: { isProtected: true, workspace }
    },
    ...children.map((child) => ({
      ...child,
      meta: { isProtected: true, workspace, ...child.meta }
    }))
  ]
})

const certificateManagement = routePaths.applications.certificateManagement
const pcfExchange = routePaths.applications.pcfExchange
const pcfExchangeV9 = routePaths.applications.pcfExchangeV9
const blockNotifications = routePaths.applications.blockNotifications
const traceability = routePaths.applications.traceability
const cyberSecurityKPIs = routePaths.applications.cyberSecurityKPIs
const electronicControlUnit = routePaths.applications.electronicControlUnit

const applicationRoutes: RouteRecordRaw[] = [
  {
    path: "/applications",
    component: () => import("@/layout/AbstractRouteView.vue"),
    meta: {
      isProtected: true
    },
    children: [
      appRoute(
        certificateManagement,
        CX_CORE_FEATURES_ENUM.CERTIFICATE_MANAGEMENT,
        EnumWorkspaceId.CERTIFICATE_MANAGEMENT,
        () =>
          import(
            "@/modules/applications/certificateManagement/pages/CertificateManagementOverview.vue"
          ),
        [
          {
            path: certificateManagement.children.publish.path,
            name: certificateManagement.children.publish.pathName,
            component: () =>
              import("@/modules/applications/certificateManagement/pages/PublishCertificate.vue"),
            meta: { pageName: EnumCertificatePageNames.CERT_PROVIDE }
          },
          {
            path: certificateManagement.children.myCertificates.path,
            name: certificateManagement.children.myCertificates.pathName,
            component: () =>
              import("@/modules/applications/certificateManagement/pages/ProvisionCertificates.vue"),
            meta: { pageName: EnumCertificatePageNames.CERT_PROVIDED }
          },
          {
            path: certificateManagement.children.certificateDetails.path,
            name: certificateManagement.children.certificateDetails.pathName,
            component: () =>
              import("@/modules/applications/certificateManagement/pages/CertificateDetails.vue")
          },
          {
            path: certificateManagement.children.sharedCertificates.path,
            name: certificateManagement.children.sharedCertificates.pathName,
            component: () =>
              import(
                "@/modules/applications/certificateManagement/components/sharedCertificates/SharedCertificates.vue"
              )
          },
          {
            path: certificateManagement.children.sharedCertificateDetails.path,
            name: certificateManagement.children.sharedCertificateDetails.pathName,
            component: () =>
              import(
                "@/modules/applications/certificateManagement/pages/SharedCertificateDetails.vue"
              )
          },
          {
            path: certificateManagement.children.sites.path,
            name: certificateManagement.children.sites.pathName,
            component: () =>
              import("@/modules/applications/certificateManagement/pages/CertificateSites.vue")
          },
          {
            path: certificateManagement.children.discover.path,
            name: certificateManagement.children.discover.pathName,
            component: SearchCatalog,
            meta: { pageName: EnumCertificatePageNames.CERT_CONSUME }
          },
          {
            path: certificateManagement.children.consumptionHistory.path,
            name: certificateManagement.children.consumptionHistory.pathName,
            component: () =>
              import(
                "@/modules/applications/certificateManagement/pages/ConsumptionCertificates.vue"
              ),
            meta: { pageName: EnumCertificatePageNames.CERT_CONSUMED }
          },
          {
            path: certificateManagement.children.inboundRequests.path,
            name: certificateManagement.children.inboundRequests.pathName,
            component: () =>
              import("@/modules/applications/certificateManagement/pages/CertInboundMessages.vue"),
            meta: { pageName: EnumCertificatePageNames.INBOUND_CERT_REQUESTS }
          },
          {
            path: certificateManagement.children.outboundStatus.path,
            name: certificateManagement.children.outboundStatus.pathName,
            component: () =>
              import("@/modules/applications/certificateManagement/pages/CertOutboundMessages.vue"),
            meta: { pageName: EnumCertificatePageNames.OUTBOUND_CERT_STATUS }
          },
          {
            path: certificateManagement.children.jobs.path,
            name: certificateManagement.children.jobs.pathName,
            component: Jobs
          }
        ]
      ),

      appRoute(
        pcfExchange,
        CX_CORE_FEATURES_ENUM.PCF_EXCHANGE,
        EnumWorkspaceId.PCF_JUPITER,
        () => import("@/modules/applications/pcfExchange/pages/PcfLanding.vue"),
        [
          {
            path: pcfExchange.children.createOffer.path,
            name: pcfExchange.children.createOffer.pathName,
            component: ConfigureOffer,
            meta: { pageName: EnumPcfPageNames.PCF_PROVIDE }
          },
          {
            path: pcfExchange.children.discover.path,
            name: pcfExchange.children.discover.pathName,
            component: SearchCatalog,
            meta: { pageName: EnumPcfPageNames.PCF_CONSUME }
          },
          {
            path: pcfExchange.children.requests.path,
            name: pcfExchange.children.requests.pathName,
            component: () => import("@/modules/applications/pcfExchange/pages/PcfRequests.vue"),
            meta: { pageName: EnumPcfPageNames.PCF_REQUESTS }
          },
          {
            path: pcfExchange.children.history.path,
            name: pcfExchange.children.history.pathName,
            component: () =>
              import("@/modules/applications/pcfExchange/pages/PcfExchangeHistory.vue"),
            meta: { pageName: EnumPcfPageNames.PCF_HISTORY }
          },
          {
            path: pcfExchange.children.jobs.path,
            name: pcfExchange.children.jobs.pathName,
            component: Jobs
          }
        ]
      ),

      appRoute(
        pcfExchangeV9,
        CX_CORE_FEATURES_ENUM.PCF_EXCHANGE,
        EnumWorkspaceId.PCF_SATURN,
        () => import("@/modules/applications/pcfExchangeV9/pages/PcfLanding.vue"),
        [
          {
            path: pcfExchangeV9.children.createOffer.path,
            name: pcfExchangeV9.children.createOffer.pathName,
            component: ConfigureOffer,
            meta: { pageName: EnumPcfV9PageNames.PCF_PROVIDE }
          },
          {
            path: pcfExchangeV9.children.discover.path,
            name: pcfExchangeV9.children.discover.pathName,
            component: SearchCatalog,
            meta: { pageName: EnumPcfV9PageNames.PCF_CONSUME }
          },
          {
            path: pcfExchangeV9.children.requests.path,
            name: pcfExchangeV9.children.requests.pathName,
            component: () => import("@/modules/applications/pcfExchangeV9/pages/PcfProvider.vue"),
            meta: { pageName: EnumPcfV9PageNames.PCF_REQUESTS }
          },
          {
            path: pcfExchangeV9.children.history.path,
            name: pcfExchangeV9.children.history.pathName,
            component: () => import("@/modules/applications/pcfExchangeV9/pages/PcfConsumer.vue"),
            meta: { pageName: EnumPcfV9PageNames.PCF_HISTORY }
          },
          {
            path: pcfExchangeV9.children.aspects.path,
            name: pcfExchangeV9.children.aspects.pathName,
            component: () => import("@/modules/applications/pcfExchangeV9/pages/PcfAspects.vue"),
            meta: { pageName: EnumPcfV9PageNames.PCF_ASPECTS }
          },
          {
            path: pcfExchangeV9.children.assets.path,
            name: pcfExchangeV9.children.assets.pathName,
            component: AssetsPage,
            meta: { pageName: EnumPcfV9PageNames.PCF_ASSETS }
          },
          {
            path: pcfExchangeV9.children.jobs.path,
            name: pcfExchangeV9.children.jobs.pathName,
            component: Jobs
          }
        ]
      ),

      appRoute(
        blockNotifications,
        CX_CORE_FEATURES_ENUM.BLOCK_NOTIFICATIONS,
        EnumWorkspaceId.BLOCK_NOTIFICATIONS,
        () =>
          import("@/modules/applications/blockNotifications/pages/BlockNotificationsLanding.vue"),
        [
          {
            path: blockNotifications.children.send.path,
            name: blockNotifications.children.send.pathName,
            component: () =>
              import("@/modules/applications/blockNotifications/pages/SendNotifications.vue"),
            meta: { pageName: EnumBnPageNames.BN_SEND }
          },
          {
            path: blockNotifications.children.sentHistory.path,
            name: blockNotifications.children.sentHistory.pathName,
            component: () =>
              import("@/modules/applications/blockNotifications/pages/SentNotificationsHistory.vue"),
            meta: { pageName: EnumBnPageNames.BN_SENT }
          },
          {
            path: blockNotifications.children.receivedHistory.path,
            name: blockNotifications.children.receivedHistory.pathName,
            component: () =>
              import(
                "@/modules/applications/blockNotifications/pages/ReceivedNotificationsHistory.vue"
              ),
            meta: { pageName: EnumBnPageNames.BN_RECEIVED }
          },
          {
            path: blockNotifications.children.jobs.path,
            name: blockNotifications.children.jobs.pathName,
            component: Jobs
          }
        ]
      ),

      appRoute(
        traceability,
        CX_CORE_FEATURES_ENUM.TRACEABILITY,
        EnumWorkspaceId.TRACEABILITY,
        () => import("@/modules/applications/traceability/pages/TraceabilityLanding.vue"),
        [
          {
            path: traceability.children.createOffer.path,
            name: traceability.children.createOffer.pathName,
            component: ConfigureOffer,
            meta: { pageName: EnumTraceabilityPageNames.TRACEABILITY_CREATE_OFFER }
          },
          {
            path: traceability.children.discover.path,
            name: traceability.children.discover.pathName,
            component: SearchCatalog,
            meta: { pageName: EnumTraceabilityPageNames.TRACEABILITY_DISCOVER }
          },
          {
            path: traceability.children.notifications.path,
            name: traceability.children.notifications.pathName,
            component: () =>
              import("@/modules/applications/traceability/pages/TraceabilityNotifications.vue"),
            meta: { pageName: EnumTraceabilityPageNames.TRACEABILITY_NOTIFICATIONS }
          },
          {
            path: traceability.children.industryCoreNotifications.path,
            name: traceability.children.industryCoreNotifications.pathName,
            component: () =>
              import(
                "@/modules/applications/traceability/pages/TraceabilityIndustryCoreNotifications.vue"
              ),
            meta: { pageName: EnumTraceabilityPageNames.TRACEABILITY_INDUSTRY_CORE_NOTIFICATIONS }
          },
          {
            path: traceability.children.contractAgreements.path,
            name: traceability.children.contractAgreements.pathName,
            component: () =>
              import("@/modules/applications/traceability/pages/TraceabilityContractAgreements.vue"),
            meta: { pageName: EnumTraceabilityPageNames.TRACEABILITY_CONTRACT_AGREEMENTS }
          },
          {
            path: traceability.children.exchangeHistory.path,
            name: traceability.children.exchangeHistory.pathName,
            component: () =>
              import("@/modules/applications/traceability/pages/TraceabilityExchangeHistory.vue"),
            meta: { pageName: EnumTraceabilityPageNames.TRACEABILITY_EXCHANGE_HISTORY }
          },
          {
            path: traceability.children.jobs.path,
            name: traceability.children.jobs.pathName,
            component: Jobs
          }
        ]
      ),

      appRoute(
        cyberSecurityKPIs,
        CX_CORE_FEATURES_ENUM.CYBERSECURITY_KPIS,
        EnumWorkspaceId.CYBER_SECURITY_METRICS,
        () => import("@/modules/applications/cybersecurityKPIs/pages/CyberSecurityKPILanding.vue"),
        [
          {
            path: cyberSecurityKPIs.children.create.path,
            name: cyberSecurityKPIs.children.create.pathName,
            component: () =>
              import("@/modules/applications/cybersecurityKPIs/pages/CyberSecurityKPICreate.vue"),
            meta: { pageName: EnumCSKPISPageNames.CSK_CREATE }
          },
          {
            path: cyberSecurityKPIs.children.jobs.path,
            name: cyberSecurityKPIs.children.jobs.pathName,
            component: Jobs
          }
        ]
      ),

      appRoute(
        electronicControlUnit,
        CX_CORE_FEATURES_ENUM.ELECTRONIC_CONTROL_UNIT,
        EnumWorkspaceId.ELECTRONIC_CONTROL_UNIT,
        () =>
          import(
            "@/modules/applications/electronicControlUnit/pages/ElectronicControlUnitLanding.vue"
          ),
        [
          {
            path: electronicControlUnit.children.sentMessages.path,
            name: electronicControlUnit.children.sentMessages.pathName,
            component: () =>
              import(
                "@/modules/applications/electronicControlUnit/pages/EcuSentMessagesHistory.vue"
              ),
            meta: { pageName: EnumEcuPageNames.ECU_SENT }
          },
          {
            path: electronicControlUnit.children.receivedMessages.path,
            name: electronicControlUnit.children.receivedMessages.pathName,
            component: () =>
              import(
                "@/modules/applications/electronicControlUnit/pages/EcuReceivedMessagesHistory.vue"
              ),
            meta: { pageName: EnumEcuPageNames.ECU_RECEIVED }
          },
          {
            path: electronicControlUnit.children.assets.path,
            name: electronicControlUnit.children.assets.pathName,
            component: AssetsPage,
            meta: { pageName: EnumEcuPageNames.ECU_ASSETS }
          },
          {
            path: electronicControlUnit.children.jobs.path,
            name: electronicControlUnit.children.jobs.pathName,
            component: Jobs,
            meta: { pageName: EnumEcuPageNames.ECU_PROCESS_LOGS }
          },
          {
            path: electronicControlUnit.children.connectors.path,
            name: electronicControlUnit.children.connectors.pathName,
            component: Connectors,
            meta: { pageName: EnumEcuPageNames.ECU_CONNECTORS }
          },
          {
            path: electronicControlUnit.children.policies.path,
            name: electronicControlUnit.children.policies.pathName,
            component: Policies,
            meta: { pageName: EnumEcuPageNames.ECU_POLICIES }
          }
        ]
      )
    ]
  }
]

export default applicationRoutes
